import React, { useState } from 'react';
import { ConfirmationModal, VerifiedBadge } from '@hofsync/design-system';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useDocumentVerification } from '../../hooks/useDocumentVerification';

export interface DocumentVerifiedBadgeContainerProps {
    /** The document currently shown in the preview. `null` hides the badge. */
    documentId: string | null;
}

/**
 * Phase 1 verified-badge surface (#495). Renders the VerifiedBadge when the
 * `documents.verified_badge` flag is on AND the viewer is an admin in
 * impersonation mode. Owns the local confirm-then-toggle flow.
 *
 * This container is the *only* place that knows about the flag + admin +
 * impersonation gating; the design-system atom stays pure.
 */
export const DocumentVerifiedBadgeContainer: React.FC<DocumentVerifiedBadgeContainerProps> = ({
    documentId,
}) => {
    const { userRoles, impersonation } = useAuth();
    const flagEnabled = useFeatureFlag('documents.verified_badge');
    const isAdmin = userRoles.includes('admin');
    const isImpersonating = impersonation?.active === true;
    const tenantId = impersonation?.user_id ?? null;

    const enabled = flagEnabled && isAdmin && isImpersonating;
    const { state, verify, unverify, toggling } = useDocumentVerification({
        tenantId,
        documentId,
        enabled,
    });

    const [confirmOpen, setConfirmOpen] = useState(false);

    if (!enabled || !documentId) return null;

    const verified = state?.verified ?? false;

    const handleClick = () => setConfirmOpen(true);
    const handleCancel = () => setConfirmOpen(false);
    const handleConfirm = async () => {
        try {
            if (verified) {
                await unverify();
            } else {
                await verify();
            }
        } finally {
            setConfirmOpen(false);
        }
    };

    const tooltip = state?.verified_at && state?.verified_by_user_id
        ? `Verifiziert von ${state.verified_by_user_id} · ${new Date(state.verified_at).toLocaleString()}`
        : undefined;

    return (
        <>
            <VerifiedBadge
                verified={verified}
                onClick={handleClick}
                tooltip={tooltip}
            />
            <ConfirmationModal
                isOpen={confirmOpen}
                title={verified ? 'Verifizierung aufheben?' : 'Dokument verifizieren?'}
                message={
                    verified
                        ? 'Möchtest du die Verifizierung dieses Dokuments aufheben?'
                        : 'Möchtest du dieses Dokument als verifiziert markieren?'
                }
                confirmLabel={verified ? 'Verifizierung aufheben' : 'Verifizieren'}
                cancelLabel="Abbrechen"
                danger={verified}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                loading={toggling}
            />
        </>
    );
};

export default DocumentVerifiedBadgeContainer;
